/* eslint-disable react/no-danger */
import React from "react";
// ANT DESIGN COMPONENTS
import { Collapse } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { faFileCode } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// INTERFACES
import {
	AdapterCdoOpcInterface,
	LocationInterface,
	OpcMappingsInterface,
	CarNum2NameEntry,
} from "../../../../../interfaces/AdapterCdoOpc.interface";
import { RootState } from "../../../../../app/store";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import DrawerRowDivider from "../../../../components/drawer/DrawerRowDivider";
import CdoOpcLocation from "./CdoOpcLocation";
import CdoOpcMappingPage from "./CdoOpcMappingPage";
import CdoOpcCarNumberToName from "./CdoOpcCarNumberToName";

export default function CdoOpcPage({
	setCdo,
	cdoValue,
}: {
	setCdo: (newValue: AdapterCdoOpcInterface) => void;
	cdoValue: AdapterCdoOpcInterface;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { theme } = useSelector((state: RootState) => state.home);
	const { Panel } = Collapse;
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeFormValue = (
		newValue: string | number,
		variableName: string,
	) => {
		const copy: AdapterCdoOpcInterface = structuredClone(cdoValue);
		if (variableName === "opc_server" && typeof newValue === "string") {
			copy.opc_server = newValue;
		} else if (variableName === "hostname" && typeof newValue === "string") {
			copy.hostname = newValue;
		} else if (
			variableName === "locationName" &&
			typeof newValue === "string"
		) {
			copy.location.name = newValue;
		}
		setCdo(copy);
	};

	const formatJsonWithColors = (obj: any) => {
		const json = JSON.stringify(obj, null, 4)
			.replace(/"([^"]+)"\s*:/g, '<span style="color:#9cdcfe;">"$1"</span>:')
			.replace(/: "([^"]*)"/g, ': <span style="color:#ce9178;">"$1"</span>')
			.replace(/:\s*(\d+)/g, ': <span style="color:#b5cea8;">$1</span>');
		return json;
	};

	const setCdoOpcLocation = (newValue: LocationInterface) => {
		const copy = structuredClone(cdoValue);
		copy.location = newValue;
		setCdo(copy);
	};

	const setCdoOpcSites = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.sites = newValue;
		setCdo(copy);
	};

	const setCdoOpcLocations = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.locations = newValue;
		setCdo(copy);
	};

	const setCdoOpcCarNumberToName = (newValue: CarNum2NameEntry[]) => {
		const copy = structuredClone(cdoValue);
		copy.car_num_to_name = newValue;
		setCdo(copy);
	};

	const setCdoOpcGroups = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.groups.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcGroupsPathOnly = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.groups.path_only.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcGroupsDataOnly = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.groups.data_only.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcGroupsPathAndData = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.groups.path_and_data.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcLifts = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.lifts.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcLiftsPathOnly = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.lifts.path_only.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcLiftsDataOnly = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.lifts.data_only.mappings = newValue;
		setCdo(copy);
	};

	const setCdoOpcLiftsPathAndData = (newValue: OpcMappingsInterface) => {
		const copy = structuredClone(cdoValue);
		copy.lifts.path_and_data.mappings = newValue;
		setCdo(copy);
	};

	// ************************************************ */
	// USE EFFECT ************************************* */

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<DrawerRowDivider icon={faFileCode} title='OPC' />
			<Collapse>
				<Panel header={`OPC JSON ${t("general.preview")}`} key='1'>
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<pre
								style={{
									width: "100%",
									color: "#dcdcdc",
									padding: "10px",
									borderRadius: "8px",
									fontSize: "14px",
									overflowX: "auto",
								}}
								dangerouslySetInnerHTML={{
									__html: formatJsonWithColors(cdoValue),
								}}
							/>
						</div>
					</div>
				</Panel>
			</Collapse>
			<br />
			<Collapse>
				<Panel header={`OPC ${t("general.formulary")}`} key='1'>
					<div className='container'>
						<div className={`drawer__box__${theme} row`}>
							<FormInputText
								inputValue={cdoValue.opc_server}
								inputLabelName='OPC Server'
								inputOnChange={onChangeFormValue}
								inputVariableName='opc_server'
								isRequired
								disabledInput={false}
								
							/>
							<FormInputText
								inputValue={cdoValue.hostname}
								inputLabelName='Hostname'
								inputOnChange={onChangeFormValue}
								inputVariableName='hostname'
								isRequired
								disabledInput={false}
								
							/>
						</div>
					</div>
				</Panel>
			</Collapse>
			{/** LOCATION */}
			<CdoOpcLocation
				setCdoOpcLocation={setCdoOpcLocation}
				location={cdoValue.location}
			/>
			{/** SITES */}
			<CdoOpcMappingPage
				setValue={setCdoOpcSites}
				title={`OPC ${t("general.sites")} Mapping`}
				opcMapping={cdoValue.sites}
			/>
			{/** LOCATIONS */}
			<CdoOpcMappingPage
				setValue={setCdoOpcLocations}
				title={`OPC ${t("general.locations")} Mapping`}
				opcMapping={cdoValue.locations}
			/>
			{/** GROUPS */}
			<CdoOpcMappingPage
				setValue={setCdoOpcGroups}
				title={`OPC ${t("general.groups")} Mapping`}
				opcMapping={cdoValue.groups.mappings}
			/>
			{/** GROUPS EXPANDED PATH ONLY */}
			<CdoOpcMappingPage
				setValue={setCdoOpcGroupsPathOnly}
				title={`OPC ${t("general.groups")} Expanded path only`}
				opcMapping={cdoValue.groups.path_only.mappings}
			/>
			{/** GROUPS EXPANDED DATA ONLY */}
			<CdoOpcMappingPage
				setValue={setCdoOpcGroupsDataOnly}
				title={`OPC ${t("general.groups")} Expanded data only`}
				opcMapping={cdoValue.groups.data_only.mappings}
			/>
			{/** GROUPS EXPANDED PATH AND DATA */}
			<CdoOpcMappingPage
				setValue={setCdoOpcGroupsPathAndData}
				title={`OPC ${t("general.groups")} Expanded path and data`}
				opcMapping={cdoValue.groups.path_and_data.mappings}
			/>
			{/** LIFTS */}
			<CdoOpcMappingPage
				setValue={setCdoOpcLifts}
				title='OPC Lifts Mapping'
				opcMapping={cdoValue.lifts.mappings}
			/>
			{/** LIFTS EXPANDED PATH ONLY */}
			<CdoOpcMappingPage
				setValue={setCdoOpcLiftsPathOnly}
				title='OPC Lifts Expanded path only'
				opcMapping={cdoValue.lifts.path_and_data.mappings}
			/>
			{/** LIFTS EXPANDED DATA ONLY */}
			<CdoOpcMappingPage
				setValue={setCdoOpcLiftsDataOnly}
				title='OPC Lifts Expanded data only'
				opcMapping={cdoValue.lifts.data_only.mappings}
			/>
			{/** LIFTS EXPANDED PATH AND DATA */}
			<CdoOpcMappingPage
				setValue={setCdoOpcLiftsPathAndData}
				title='OPC Lifts Expanded path and data'
				opcMapping={cdoValue.lifts.path_and_data.mappings}
			/>
			{/** CAR TO NUMBER NAME */}
			<CdoOpcCarNumberToName
				setCdoOpcCarNumberToName={setCdoOpcCarNumberToName}
				carNumbersToNames={cdoValue.car_num_to_name}
			/>
		</div>
	);
}
